import React, { useState } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const TEMPLATE_PAGE_WIDTH = 793.7;
const PDF_X_MARGIN = 48;

const WrapperPage = styled(Box)`
    display: flex;
    justify-content: center;
    box-shadow: 20px 2px 17px 0 rgba(120, 112, 178, 0.08);
    margin-bottom: 24px;
    position: relative;
`;

const PdfViewer = ({ document }) => {
    const { t } = useTranslation();
    const [numPages, setNumPages] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [scalePage, setScalePage] = useState(1);

    const handleCloseLoading = () => setIsLoading(true);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setIsLoading(true);
    };

    const onPageLoad = (page) => {
        const scale = (window.innerWidth - PDF_X_MARGIN) / page.originalWidth;
        if (scale !== scalePage) {
            setScalePage(scale);
        }
    };

    return (
        <Box display="flex" flexDirection="column" alignItems="center">
            <Document
                error={t('Failed to load PDF file')}
                file={document}
                loading={() =>
                    isLoading ? (
                        <Box
                            alignItems="center"
                            display="flex"
                            flexDirection="column"
                            gap={2}
                        >
                            <CircularProgress />
                            <Box
                                alignItems="center"
                                display="flex"
                                flexDirection="column"
                                gap={1}
                            >
                                <Typography variant="caption">
                                    {t('Loading document').concat('...')}
                                </Typography>
                                <Typography variant="caption">
                                    {t('Please wait')}
                                </Typography>
                            </Box>
                        </Box>
                    ) : null
                }
                onLoadError={handleCloseLoading}
                onLoadSuccess={onDocumentLoadSuccess}
                onSourceSuccess={handleCloseLoading}
            >
                {Array.from(new Array(numPages)).map((el, index) => (
                    <Box sx={{ width: '100%' }} key={`page-${index}`}>
                        <WrapperPage
                            sx={{ display: { xs: 'none', lg: 'block' } }}
                        >
                            <Page
                                pageNumber={index + 1}
                                width={TEMPLATE_PAGE_WIDTH}
                            />
                        </WrapperPage>
                        <WrapperPage
                            sx={{ display: { xs: 'block', lg: 'none' } }}
                        >
                            <Page
                                pageNumber={index + 1}
                                onLoadSuccess={onPageLoad}
                                scale={scalePage}
                            />
                        </WrapperPage>
                    </Box>
                ))}
            </Document>
        </Box>
    );
};

PdfViewer.propTypes = {
    document: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

export default PdfViewer;
